import { AbstractControl, FormControl, FormGroupDirective, FormsModule, NgControl, NgForm, ValidatorFn } from '@angular/forms';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LookUp, Hospital, Insurer} from '../../../models/look-up.model';
import { StateService } from '../../../services/state.service';
import { Component, Input, OnInit, Self } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

class InputErrorStateMatcher implements ErrorStateMatcher {
    constructor(private innerControl: NgControl, private forceValidation: boolean = false) { }

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (!form?.submitted) {
            return control?.touched === true && this.errorsIgnoreRequired(this.innerControl.control!, this.forceValidation);
        }

        return this.innerControl.invalid === true;
    }

    errorsIgnoreRequired(control: AbstractControl | null, forceValidation: boolean): boolean {
        if(forceValidation && this.innerControl.invalid){
            return true; 
        }

        if (!control?.errors) {
            return false;
        }

        if (control.errors['required'] === true ) {
            return false;
        }

        return true;
    }
}
@Component({
    selector: 'minster-select',
    standalone: true,
    imports: [FormsModule, CommonModule, MatFormFieldModule, MatSelectModule, MatTooltipModule, MatIconModule],
    templateUrl: './minster-select.component.html',
    styleUrls: ['./minster-select.component.css']
})

export class MinsterSelectComponent extends ControlBaseComponent<number> implements OnInit {
    _lookUps: LookUp[] = [];
    _visibleLookUps: LookUp[] = [];
    _matcher = new InputErrorStateMatcher(this.controlDir);
    
    @Input() override _toolTip: string = '';
    @Input('sort') _sort: boolean = false;
    @Input('startsAtZero') _startsAtZero: boolean = false;
    @Input('forceValidation') _forceValidation: boolean = false;
    @Input('displayNa') _displayNa: boolean = false;
    @Input('excludeIds') _excludeIds: number[] = [];
    @Input('dynamicLookUp') _dynamicLookUp: boolean = false;
    @Input() set filteredLookUps(value: LookUp[]) {
        this._visibleLookUps = value || [];
    }

    @Input() set lookUp(value: LookUp[] | Hospital[] | Insurer[]) {
        if (value && value.length > 0) {
            const isHospitalList = 'hospitalId' in value[0];
            const isInsurerList = 'insurerId' in value[0];

            if (isHospitalList) {
                this._lookUps = (value as Hospital[]).map(hospital => ({
                    description: hospital.description,
                    isSelectable: hospital.isActive,
                    value: hospital.hospitalId
                }));
            } 
            else if(isInsurerList){
                 this._lookUps = (value as Insurer[]).map(insurer => ({
                    description: insurer.insurerName,
                    isSelectable: insurer.isActive,
                    value: insurer.insurerId
                }));
            }
            else {
                this._lookUps = value as LookUp[];
            }

            if (!this._dynamicLookUp) {
                this.populateLookUps();
            } 
            else {
                this._visibleLookUps = [...this._lookUps];
            }
        }
        
        else {
            this._lookUps = [];
            this._visibleLookUps = [];
        }
    }
    
    constructor(@Self() public controlDir: NgControl, stateService: StateService) {
        super(controlDir, stateService);
    }

    ngOnInit(): void {
        this.setup();
        this.populateLookUps();

        const control = this.controlDir.control;
        const myValidators: ValidatorFn[] = [];

        if (this._required && !this._disabled) {
            myValidators.push((control: AbstractControl) => nullValidator(control, this._startsAtZero, this._forceValidation));
        }

        if (control) {
            control.setValidators(myValidators);
            control.updateValueAndValidity();
        }

        this._matcher = new InputErrorStateMatcher(this.controlDir, this._forceValidation);
    }

    populateLookUps() {
        let lookUps = this._lookUps.filter(
            x => (x.isSelectable || x.value === this._value) && !this._excludeIds.includes(x.value!)
        );

        if (this._sort) {
            lookUps = lookUps.sort((a, b) => a.description!.localeCompare(b.description!));
        }

        if (this._displayNa && !lookUps.some(x => x.value === null)) {
            lookUps.splice(0, 0, { description: 'N/A', value: undefined, isSelectable: true });
        }

        this._visibleLookUps = lookUps;
    }

    override writeValue(value: any): void {
        this._value = value;

        if (Number(value) > 0 && !this._dynamicLookUp) {
            this.populateLookUps();
        }
    }

    valueUpdated(newValue: number) {
        this._value = newValue;
        this._propagateChange(this._value);

        if (newValue === null) {
            this.controlDir.control?.setValue(null);
        }
    }
}

function nullValidator(control: AbstractControl, startsAtZero: boolean, forceValidation: boolean): { [key: string]: boolean } | null {
    const value = control.value;
    const isInvalid = value === 'Please select' || (!startsAtZero && (value === 0 || value === undefined));
    return isInvalid ? { required: true } : null;
}